/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  CheckCircle2, 
  AlertTriangle, 
  FileText, 
  Download, 
  Printer, 
  Clock, 
  History,
  Eye,
  Beaker,
  FlaskConical,
  User,
  ShieldCheck,
  Search
} from 'lucide-react';
import { SterilityTestServices } from '../../../service/sterilityTestServices';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';

interface SterilityObservationDetailsProps {
  onBack: () => void;
  onViewTest: () => void;
  sterilityTestId: string;
}

const SterilityObservationDetails: React.FC<SterilityObservationDetailsProps> = ({ onBack, onViewTest,sterilityTestId }) => {
  const { t } = useTranslation();

  interface Observation {
    day: string;
    status: string;
    date: string;
    result: string;
    observationResult?: string;
    clarity?: string;
    recordedBy?: string;
    indicators?: {
      colorChange: string;
      gasFormation: string;
      visibleParticles: string;
      surfaceFilm: string;
      sedimentFormation: string;
    };
    floraIdentification?: {
      genus: string;
      species: string;
      morphology: string;
      idMethod: string;
    };
    remarks: string;
  }

  interface Tube {
    id: string;
    media: string;
    mediaBatch: string;
    type: string;
    status: string;
    result: string;
    observations: Observation[];
  }

  // Raw row returned by the observation-schedule API
  interface ObservationScheduleRow {
    schedule_id: string;
    observation_day: string;
    scheduled_date_time: string | null;
    observation_date_time: string | null;
    schedule_status: string | null;
    schedule_operator_id: string | null;
    tube_id: string;
    mt_serial_number: string | null;
    mt_batch_number: string | null;
    sterility_test_identifier: string | null;
    product_name: string | null;
    observation_id: string | null;
    visual_clarity_assessment: string | null;
    color_change: string | boolean | null;
    visible_particles: string | boolean | null;
    sediment_formation: string | boolean | null;
    gas_formation: string | boolean | null;
    surface_film: string | boolean | null;
    remarks: string | null;
    observation_results: string | null;
  }

  interface ParentTest {
    testId: string;
    productName: string;
    productBatch: string;
    testMethod: string;
    lotNumber: string;
    status: string;
    tubes: Tube[];
  }

  const EMPTY_PARENT_TEST: ParentTest = {
    testId: '-',
    productName: '-',
    productBatch: '-',
    testMethod: '-',
    lotNumber: '-',
    status: 'In Progress',
    tubes: []
  };

  const EMPTY_TUBE: Tube = {
    id: '-',
    media: '-',
    mediaBatch: '-',
    type: '',
    status: '-',
    result: 'Pending',
    observations: []
  };

  // ---------- API -> UI mapping helpers ----------
  const toYesNo = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';
    const v = String(value).trim().toLowerCase();
    if (['yes', 'y', 't', 'true', '1'].includes(v)) return 'Yes';
    if (['no', 'n', 'f', 'false', '0'].includes(v)) return 'No';
    return String(value);
  };

  const toTitleCase = (value: string | null): string => {
    if (!value) return '-';
    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // observation_results -> PASS / FAIL / '-'
  const toResult = (raw: string | null): string => {
    if (!raw) return '-';
    const v = raw.toUpperCase().replace(/_/g, ' ');
    if (v.includes('PASS') || v.includes('NO GROWTH')) return 'PASS';
    if (v.includes('FAIL') || v.includes('GROWTH')) return 'FAIL';
    return '-';
  };

  const getResultBadge = (result: string, selected = false): string => {
    if (result === 'FAIL') {
      return selected
        ? 'bg-rose-500 text-white border-rose-500 shadow-sm shadow-rose-500/20'
        : 'bg-rose-50 text-rose-600 border-rose-100';
    }
    if (result === 'PASS') {
      return selected
        ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm shadow-emerald-500/20'
        : 'bg-emerald-50 text-emerald-600 border-emerald-100';
    }
    return selected
      ? 'bg-slate-500 text-white border-slate-500'
      : 'bg-slate-50 text-slate-500 border-slate-100';
  };

  const mapRowToObservation = (row: ObservationScheduleRow): Observation => {
    const isCompleted = (row.schedule_status ?? '').toUpperCase() === 'COMPLETED';
    return {
      day: `Day ${row.observation_day}`,
      status: isCompleted ? 'Completed' : 'Pending',
      date: row.observation_date_time || row.scheduled_date_time || '-',
      result: toResult(row.observation_results),
      observationResult: toTitleCase(row.observation_results),
      clarity: row.visual_clarity_assessment || '-',
      recordedBy: row.schedule_operator_id ? `Operator ${row.schedule_operator_id}` : '-',
      indicators: {
        colorChange: toYesNo(row.color_change),
        gasFormation: toYesNo(row.gas_formation),
        visibleParticles: toYesNo(row.visible_particles),
        surfaceFilm: toYesNo(row.surface_film),
        sedimentFormation: toYesNo(row.sediment_formation)
      },
      remarks: row.remarks || '-'
    };
  };

  // Group flat schedule rows by tube and derive tube-level status/result
  const buildParentTest = (rows: ObservationScheduleRow[]): ParentTest => {
    if (!rows.length) return EMPTY_PARENT_TEST;

    const first = rows[0];
    const grouped = new Map<string, ObservationScheduleRow[]>();
    rows.forEach((row) => {
      const list = grouped.get(row.tube_id) ?? [];
      list.push(row);
      grouped.set(row.tube_id, list);
    });

    const tubes: Tube[] = Array.from(grouped.entries()).map(([tubeId, tubeRows]) => {
      const observations = [...tubeRows]
        .sort((a, b) => Number(a.observation_day) - Number(b.observation_day))
        .map(mapRowToObservation);

      const completed = observations.filter((o) => o.status === 'Completed');
      const hasFail = observations.some((o) => o.result === 'FAIL');
      const allCompleted = observations.length > 0 && completed.length === observations.length;
      const allPass = allCompleted && observations.every((o) => o.result === 'PASS');

      return {
        id: tubeRows[0].mt_serial_number || `TUBE-${tubeId}`,
        media: '-', // media type (FTM/SCDM) is not part of this API response
        mediaBatch: tubeRows[0].mt_batch_number || '-',
        type: '',
        status: hasFail ? 'Failed' : allCompleted ? 'Completed' : completed.length > 0 ? 'Ongoing' : 'Pending',
        result: hasFail ? 'FAIL' : allPass ? 'PASS' : 'Pending',
        observations
      };
    });

    const anyFail = tubes.some((t) => t.result === 'FAIL');
    const allDone = tubes.every((t) => t.status === 'Completed');

    return {
      testId: first.sterility_test_identifier || '-',
      productName: first.product_name || '-',
      productBatch: '-',
      testMethod: '-',
      lotNumber: '-',
      status: anyFail ? 'Failed' : allDone ? 'Completed' : 'In Progress',
      tubes
    };
  };

  // Default day to show for a tube: latest completed observation, else the first one
  const getDefaultDay = (tube: Tube): string => {
    const completed = tube.observations.filter((o) => o.status === 'Completed');
    return (completed[completed.length - 1] ?? tube.observations[0])?.day ?? '';
  };

  // State
  const [parentTest, setParentTest] = useState<ParentTest>(EMPTY_PARENT_TEST);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTubeId, setSelectedTubeId] = React.useState<string>('');
  const [selectedDay, setSelectedDay] = React.useState<string>('');
  const [sort, setSort] = useState("scheduled_date_time");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);


  const selectedTube: Tube = parentTest.tubes.find(t => t.id === selectedTubeId) || parentTest.tubes[0] || EMPTY_TUBE;
  const selectedObservation: Observation | undefined = selectedTube.observations.find(o => o.day === selectedDay) || selectedTube.observations[0];

  const auditTrail = [
    { action: 'Observation Recorded', user: 'P Shyam Kumar', timestamp: '19-Aug-2024 10:45:12', oldVal: 'Pending', newVal: 'Completed' },
    { action: 'System Validation', user: 'System-Auto', timestamp: '19-Aug-2024 10:45:15', oldVal: '-', newVal: 'Pass' },
    { action: 'QA Review', user: 'Harish S', timestamp: '19-Aug-2024 11:30:00', oldVal: 'Unverified', newVal: 'Verified' }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Completed': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'In Progress': return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'Failed': return 'bg-rose-50 text-rose-600 border-rose-100';
      default: return 'bg-slate-50 text-slate-500 border-slate-100';
    }
  };

  const getSterilityObservationList = async (sterilityTestId: string) => {
    setIsLoading(true);
    try {
      const response = await SterilityTestServices.getSterilityObservationList(page, pageSize, sort, order, sterilityTestId);

      if (response.status && response.statusCode === 200) {
        const rows: ObservationScheduleRow[] = response.data ?? [];
        const mapped = buildParentTest(rows);
        setParentTest(mapped);

        const firstTube = mapped.tubes[0];
        if (firstTube) {
          setSelectedTubeId(firstTube.id);
          setSelectedDay(getDefaultDay(firstTube));
        } else {
          setSelectedTubeId('');
          setSelectedDay('');
        }
      }
    } catch (error) {
      console.error('Sterility observation list error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSterilityObservationList(sterilityTestId);
  }, [sterilityTestId])

  return (
    <div className="min-h-screen bg-slate-50/30 flex flex-col media-prep-scope">
      {/* Header Sticky Area */}
      <header className="bg-white border-b border-slate-200 px-8 py-4 sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0F172A] hover:bg-slate-100 transition-all shadow-sm"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="space-y-0.5">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-black text-[#0F172A] tracking-tight">{parentTest.testId}</h1>
                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStatusBadge(parentTest.status)}`}>
                  {parentTest.status}
                </span>
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 text-slate-500 text-[9px] font-black rounded uppercase tracking-widest border border-slate-200">
                   <ShieldCheck className="w-3 h-3 text-emerald-500" />
                   {t('sterilityObservationDetails.header.readOnlyRecord')}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-400 font-bold text-[10px] uppercase tracking-tighter">
                <div className="flex items-center gap-1.5"><span className="text-slate-300">{t('sterilityObservationDetails.header.product')}</span> {parentTest.productName} ({parentTest.productBatch})</div>
                <div className="flex items-center gap-1.5"><span className="text-slate-300">{t('sterilityObservationDetails.header.method')}</span> {parentTest.testMethod}</div>
                <div className="flex items-center gap-1.5"><span className="text-slate-300">{t('sterilityObservationDetails.header.lot')}</span> {parentTest.lotNumber}</div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 font-black rounded-xl hover:bg-slate-50 transition-all text-[9px] uppercase tracking-widest shadow-sm">
              <Download className="w-4 h-4" />
              {t('sterilityObservationDetails.header.actions.download')}
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 font-black rounded-xl hover:bg-slate-50 transition-all text-[9px] uppercase tracking-widest shadow-sm">
              <Printer className="w-4 h-4" />
              {t('sterilityObservationDetails.header.actions.print')}
            </button>
            {/* <button 
              onClick={onViewTest}
              className="flex items-center gap-2 px-6 py-2 bg-[#0F172A] text-white font-black rounded-xl shadow-lg shadow-slate-900/10 hover:scale-105 transition-all text-[9px] uppercase tracking-widest"
            >
              <FileText className="w-4 h-4" />
              {t('sterilityObservationDetails.header.actions.viewTest')}
            </button> */}
          </div>
        </div>
      </header>
      {isLoading && <div className="h-0.5 bg-blue-500 animate-pulse" />}

      <main className="flex-1 max-w-[1600px] mx-auto w-full p-6 grid grid-cols-1 xl:grid-cols-12 gap-6 pb-32">
        
        {/* Left Section - Tube List Hierarchy */}
        <div className="xl:col-span-3 space-y-4">
          <div className="bg-white rounded-[2rem] border border-slate-200/60 overflow-hidden shadow-sm sticky top-24">
            <div className="p-5 border-b border-slate-50 bg-slate-50/50">
              <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest flex items-center gap-2">
                <Beaker className="w-4 h-4 text-orange-500" />
                {t('sterilityObservationDetails.tubeList.title')}
              </h3>
            </div>
            
            <div className="p-3 space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto custom-scrollbar">
              {parentTest.tubes.map((tube) => (
                <button
                  key={tube.id}
                  onClick={() => {
                    setSelectedTubeId(tube.id);
                    setSelectedDay(getDefaultDay(tube));
                  }}
                  className={`w-full text-left p-4 rounded-2xl border transition-all relative group
                    ${selectedTubeId === tube.id 
                      ? 'bg-[#0F172A] border-[#0F172A] text-white shadow-lg' 
                      : 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                    }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${selectedTubeId === tube.id ? 'text-slate-400' : 'text-slate-400'}`}>
                      {tube.media}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter border
                      ${tube.result === 'PASS' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 
                        tube.result === 'FAIL' ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' : 
                        'bg-slate-100 text-slate-400 border-slate-200'
                      }`}
                    >
                      {tube.status}
                    </span>
                  </div>
                  <h4 className="text-sm font-black tracking-tight">{tube.id}</h4>
                  <p className={`text-[9px] font-bold mt-1 uppercase ${selectedTubeId === tube.id ? 'text-slate-400' : 'text-slate-400'}`}>
                    {t('sterilityObservationDetails.tubeList.observationsCompleted', { completed: tube.observations.filter(o => o.status === 'Completed').length })}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Center Section - Observation Details */}
        <div className="xl:col-span-6 space-y-4">
          {/* Observation Tabs */}
          <div className="bg-white rounded-[2rem] border border-slate-200 p-5 shadow-sm">
             <div className="flex items-center justify-between mb-4 px-1">
                <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest flex items-center gap-2">
                  <History className="w-4 h-4 text-blue-500" />
                  {t('sterilityObservationDetails.history.title')}
                </h3>
             </div>
             <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {selectedTube.observations.map((obs) => {
                  const day = obs.day;
                  const isCompleted = obs.status === 'Completed';
                  const isSelected = day === selectedDay;

                  return (
                    <button
                      key={day}
                      disabled={!isCompleted}
                      onClick={() => setSelectedDay(day)}
                      className={`flex flex-col p-4 rounded-2xl border transition-all relative group
                        ${isSelected 
                          ? 'bg-blue-50 border-blue-200 shadow-md ring-4 ring-blue-500/5' 
                          : isCompleted
                            ? 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50 shadow-sm'
                            : 'bg-slate-50 border-transparent text-slate-300 cursor-not-allowed opacity-50'
                        }`}
                    >
                      <span className={`text-[10px] font-black uppercase tracking-widest mb-1.5 transition-colors ${isSelected ? 'text-blue-600' : 'text-slate-400'}`}>{day}</span>
                      <div className="flex items-center justify-between gap-3 w-full">
                         <span className={`text-[11px] font-black transition-colors ${isSelected ? 'text-[#0F172A]' : 'text-slate-600 group-hover:text-slate-900'}`}>
                           {isCompleted ? obs.date.split(' ')[0] : t('sterilityObservationDetails.history.scheduled')}
                         </span>
                         {isCompleted && (
                           <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-full border transition-all ${getResultBadge(obs.result, isSelected)}`}>
                             {obs.result}
                           </span>
                         )}
                      </div>
                      {isSelected && (
                        <div className="absolute top-0 right-6 w-8 h-1 bg-blue-500 rounded-b-full shadow-sm"></div>
                      )}
                    </button>
                  );
                })}
             </div>
          </div>

          {selectedObservation && selectedObservation.status === 'Completed' ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
              {/* Detailed Result Card */}
              <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-50 bg-slate-50/30 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                      <Eye className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-[#0F172A] tracking-tight">{t('sterilityObservationDetails.assessment.title', { day: selectedDay })}</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('sterilityObservationDetails.assessment.recordedBy', { user: selectedObservation.recordedBy })}</p>
                    </div>
                  </div>
                  <span className={`px-4 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest border ${getResultBadge(selectedObservation.result)}`}>
                    {selectedObservation.observationResult}
                  </span>
                </div>

                <div className="p-8 space-y-8">
                  {/* Visual Clarity */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityObservationDetails.assessment.visualClarity')}</label>
                      <p className="text-sm font-black text-[#0F172A] px-4 py-3 bg-slate-50 rounded-xl border border-slate-100">
                        {selectedObservation.clarity}
                      </p>
                    </div>
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityObservationDetails.assessment.recordingTimestamp')}</label>
                      <p className="text-sm font-black text-[#0F172A] px-4 py-3 bg-slate-50 rounded-xl border border-slate-100">
                        {selectedObservation.date}
                      </p>
                    </div>
                  </div>

                  {/* Contamination Indicators */}
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityObservationDetails.assessment.contaminationIndicators')}</label>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                      {Object.entries(selectedObservation.indicators ?? {}).map(([key, val]) => (
                        <div key={key} className={`p-4 rounded-2xl border flex flex-col items-center justify-center text-center
                          ${val === 'Yes' ? 'bg-rose-50 border-rose-100' : 'bg-slate-50 border-slate-100'}
                        `}>
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tight mb-1">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className={`text-xs font-black ${val === 'Yes' ? 'text-rose-600' : 'text-slate-700'}`}>
                            {val}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityObservationDetails.assessment.technicianRemarks')}</label>
                    <div className="p-5 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-medium text-slate-700 leading-relaxed italic">
                      "{selectedObservation.remarks}"
                    </div>
                  </div>

                  {/* Flora Identification - Only if Growth Detected */}
                  {selectedObservation.observationResult === 'Growth Detected' && selectedObservation.floraIdentification && (
                    <div className="mt-8 pt-8 border-t border-slate-100 space-y-4">
                      <div className="flex items-center gap-2 text-rose-600">
                        <AlertTriangle className="w-4 h-4" />
                        <h4 className="text-[10px] font-black uppercase tracking-widest">{t('sterilityObservationDetails.assessment.flora.title')}</h4>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-rose-50/30 border border-rose-100 rounded-2xl">
                          <p className="text-[9px] font-bold text-rose-400 uppercase mb-1">{t('sterilityObservationDetails.assessment.flora.genus')}</p>
                          <p className="text-xs font-black text-rose-700">{selectedObservation.floraIdentification.genus}</p>
                        </div>
                        <div className="p-4 bg-rose-50/30 border border-rose-100 rounded-2xl">
                          <p className="text-[9px] font-bold text-rose-400 uppercase mb-1">{t('sterilityObservationDetails.assessment.flora.species')}</p>
                          <p className="text-xs font-black text-rose-700">{selectedObservation.floraIdentification.species}</p>
                        </div>
                        <div className="p-4 bg-rose-50/30 border border-rose-100 rounded-2xl">
                          <p className="text-[9px] font-bold text-rose-400 uppercase mb-1">{t('sterilityObservationDetails.assessment.flora.method')}</p>
                          <p className="text-xs font-black text-rose-700">{selectedObservation.floraIdentification.idMethod}</p>
                        </div>
                        <div className="p-4 bg-rose-50/30 border border-rose-100 rounded-2xl">
                          <p className="text-[9px] font-bold text-rose-400 uppercase mb-1">{t('sterilityObservationDetails.assessment.flora.morphology')}</p>
                          <p className="text-[10px] font-black text-rose-700 leading-tight">{selectedObservation.floraIdentification.morphology}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Audit Summary Section */}
              {/* <div className="bg-white rounded-[2rem] border border-slate-200/60 p-6 shadow-sm">
                <h3 className="text-[10px] font-black text-[#0F172A] uppercase tracking-widest mb-4 flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-purple-500" />
                  {t('sterilityObservationDetails.audit.title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {auditTrail.map((log, idx) => (
                    <div key={idx} className="flex flex-col p-3 bg-slate-50/50 rounded-xl border border-slate-100">
                      <span className="text-[8px] font-black text-slate-400 uppercase mb-1">{log.action}</span>
                      <span className="text-[10px] font-black text-slate-800">{log.user}</span>
                      <span className="text-[9px] font-bold text-slate-400 mt-1">{log.timestamp}</span>
                    </div>
                  ))}
                </div>
              </div> */}
            </div>
          ) : (
            <div className="bg-white rounded-[2.5rem] border border-slate-200 border-dashed p-20 flex flex-col items-center justify-center text-center space-y-4">
               <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center text-slate-200">
                  <Search className="w-8 h-8" />
               </div>
               <div>
                  <h3 className="text-sm font-black text-[#0F172A] uppercase tracking-tight">{t('sterilityObservationDetails.empty.title')}</h3>
                  <p className="text-xs font-bold text-slate-400 max-w-[280px] mx-auto mt-2">
                    {t('sterilityObservationDetails.empty.description')}
                  </p>
               </div>
            </div>
          )}
        </div>

        {/* Right Section - Unit Summary */}
        <div className="xl:col-span-3 space-y-6">
          <div className="bg-white rounded-[2rem] border border-slate-200/60 p-8 shadow-xl shadow-slate-200/20 sticky top-24">
             <div className="space-y-6">
                <div className="text-center pb-6 border-b border-slate-50">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{t('sterilityObservationDetails.unitSummary.title')}</p>
                  <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center text-white shadow-lg mb-4
                    ${selectedTube.result === 'PASS' ? 'bg-emerald-500 shadow-emerald-500/20' : 
                      selectedTube.result === 'FAIL' ? 'bg-rose-500 shadow-rose-500/20' : 
                      'bg-slate-200 shadow-slate-200/20'}
                  `}>
                    {selectedTube.result === 'PASS' ? <CheckCircle2 className="w-12 h-12" /> : 
                     selectedTube.result === 'FAIL' ? <AlertTriangle className="w-12 h-12" /> : 
                     <Clock className="w-10 h-10" />}
                  </div>
                  <h4 className="text-2xl font-black text-[#0F172A] tracking-widest">{selectedTube.result}</h4>
                  <p className="text-[10px] font-black text-slate-400 uppercase mt-1">{t('sterilityObservationDetails.unitSummary.finalDisposition')}</p>
                </div>

                <div className="space-y-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-black text-slate-400 uppercase">{t('sterilityObservationDetails.unitSummary.selectedUnit')}</span>
                    <p className="text-xs font-black text-[#0F172A]">{selectedTube.id}</p>
                  </div>
                  <div 
                    className="flex flex-col gap-1 cursor-pointer group"
                    onClick={() => {
                      if (selectedObservation) {
                        setSelectedDay(selectedObservation.day);
                      }
                    }}
                  >
                    <span className="text-[9px] font-black text-slate-400 uppercase group-hover:text-blue-500 transition-colors">{t('sterilityObservationDetails.unitSummary.observationDate')}</span>
                    <p className="text-xs font-black text-blue-600 underline underline-offset-4 decoration-blue-200 group-hover:text-blue-700 transition-colors">
                      {selectedObservation?.date?.split(' ')[0] || t('sterilityObservationDetails.unitSummary.notAvailable')}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-black text-slate-400 uppercase">{t('sterilityObservationDetails.unitSummary.mediaType')}</span>
                    <p className="text-xs font-black text-[#0F172A]">{selectedTube.media}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-black text-slate-400 uppercase">{t('sterilityObservationDetails.unitSummary.mediaBatch')}</span>
                    <p className="text-xs font-black text-[#0F172A]">{selectedTube.mediaBatch}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-black text-slate-400 uppercase">{t('sterilityObservationDetails.unitSummary.tubeStatus')}</span>
                    <p className={`text-[10px] font-black uppercase ${selectedTube.status === 'Failed' ? 'text-rose-600' : 'text-blue-600'}`}>{selectedTube.status}</p>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-50 space-y-3">
                   <div className="p-4 bg-blue-50/30 rounded-2xl border border-blue-100 border-dashed">
                      <div className="flex gap-3 items-start mb-2">
                         <ShieldCheck className="w-4 h-4 text-blue-500 shrink-0" />
                         <span className="text-[10px] font-black text-blue-900 uppercase tracking-tighter">{t('sterilityObservationDetails.unitSummary.compliance.title')}</span>
                      </div>
                      <p className="text-[10px] font-bold text-blue-700 leading-tight">
                        {t('sterilityObservationDetails.unitSummary.compliance.description')}
                      </p>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SterilityObservationDetails;